/* API Context */
import {getRequest, postRequest, postRequestWithoutToken} from "../fetchWrapper";

/**
 * Récupération de la liste de tous les défunts.
 *
 * @param string $token Jeton d'authentification.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getAdminDeceasedList = (token, pageNumber, itemsPerPage, keywords, dateFilter) => {
    const url = `/api-v1/admin/deceased/${pageNumber}/${itemsPerPage}/${keywords}/${dateFilter}/list`
    return getRequest(url, token)
}

/**
 * Récupération de la liste des défunts pour une agence.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant de l'agence.
 * @param int $pageNumber Numéro de la page.
 * @param int $itemsPerPage Nombre d'éléments par page.
 * @param string $keywords Mots-clés de recherche (optionnel).
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedList = (token, id, pageNumber, itemsPerPage, keywords = '') => {
    const url = keywords
        ? `/api-v1/deceased/${id}/${pageNumber}/${itemsPerPage}/list?search=${encodeURIComponent(keywords)}`
        : `/api-v1/deceased/${id}/${pageNumber}/${itemsPerPage}/list`
    return getRequest(url, token)
}

/**
 * Récupération des informations d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedInfos = (token, id) => {
    const url = `/api-v1/deceased/${id}/infos`
    return getRequest(url, token)
}

/**
 * Récupération des informations du gestionnaire d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedAdministrator = (token, id) => {
    const url = `/api-v1/deceased/${id}/administrator`
    return getRequest(url, token)
}

/**
 * Récupération des informations du conjoint d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedPartner = (token, id) => {
    const url = `/api-v1/deceased/${id}/partner`
    return getRequest(url, token)
}

/**
 * Récupération de la liste des enfants d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedChildren = (token, id) => {
    const url = `/api-v1/deceased/${id}/child/list`
    return getRequest(url, token)
}

/**
 * Récupération des informations du correspondant d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedCorrespondent = (token, id) => {
    const url = `/api-v1/deceased/${id}/correspondent`
    return getRequest(url, token)
}

/**
 * Mise à jour des informations générales d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedGeneralInfos = (token, body) => {
    const url = `/api-v1/deceased/general/infos`
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations de naissance d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedBirthInfos = (token, body) => {
    const url = `/api-v1/deceased/general/birth/infos`
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations de décès d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedDeathInfos = (token, body) => {
    const url = `/api-v1/deceased/general/death/infos`
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations de décès d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const uploadDeceasedPortrait = (token, body) => {
    const url = `/api-v1/deceased/portrait/upload`;
    return postRequest(url, body, token)
};

/**
 * Mise à jour du portrait d'un défunt choisi dans la bibliothèque.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedPortraitFromLib = (token, body) => {
    const url = `/api-v1/deceased/portrait/library`;
    return postRequest(url, body, token)
};

/**
 * Mise à jour du statut familial d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedFamilyStatus = (token, body) => {
    const url = `/api-v1/deceased/family/status`;
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations du conjoint d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedPartnerInfos = (token, body) => {
    const url = `/api-v1/deceased/partner`;
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations de naissance du conjoint d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedPartnerBirthInfos = (token, body) => {
    const url = `/api-v1/deceased/partner/birth/infos`;
    return postRequest(url, body, token)
}

/**
 * Suppresssion d'un enfant d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant de l'enfant.
 */
export const deleteChildFromDb = (token, body) => {
    const url = `/api-v1/deceased/child/delete`
    return postRequest(url, body, token)
};

/**
 * Création d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const createDeceased = (token, body) => {
    const url = `/api-v1/deceased/create`
    return postRequest(url, body, token)
}

/**
 * Mise à jour du régime matrimonial d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateMatrimonialRegime = (token, body) => {
    const url = `/api-v1/deceased/partner/regime`
    return postRequest(url, body, token)
};

/**
 * Lecture des informations du tuteur légal d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getDeceasedTutor = (token, id) => {
    const url = `/api-v1/deceased/${id}/tutor`
    return getRequest(url, token)
};

/**
 * Lecture des listes d'affiliation.
 *
 * @param string $token Jeton d'authentification.
 */
export const getAffiliationList = (token) => {
    const url = `/api-v1/affiliation/list`
    return getRequest(url, token)
}

/**
 * Ecriture d'un enfant d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 */
export const updateChild = (token, body) => {
    const url = `/api-v1/deceased/child/update`
    return postRequest(url, body, token)
};

/**
 * Mise à jour des informations du gestionnaire d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedAdministrator = (token, body) => {
    const url = `/api-v1/deceased/administrator/update`
    return postRequest(url, body, token)
}

/**
 * Mise à jour des informations du correspondant d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedCorrespondent = (token, body) => {
    const url = `/api-v1/deceased/correspondent/update`
    return postRequest(url, body, token)
}

/**
 * Mise à jour du signataire des courriers d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const updateDeceasedSignatory = (token, body) => {
    const url = `/api-v1/deceased/signatory/update`
    return postRequest(url, body, token)
}

/**
 * Suppression d'un produit.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Identifiant du produit à supprimer.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const deleteDeceasedFromDb = (token, body) => {
    const url = `/api-v1/deceased/delete`
    return postRequest(url, body, token)
};

/**
 * Changement du statut d'activation d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const setDeceasedEnabled = (token, body) => {
    const url = `/api-v1/admin/deceased/enable`
    return postRequest(url, body, token)
};

/**
 * Récupération des informations de complétion d'un défunt.
 *
 * @param string $token Jeton d'authentification.
 * @param int $id Identifiant du défunt.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const getCompletionStatus = (token, id) => {
    const url = `/api-v1/deceased/${id}/completion/status`
    return getRequest(url, token)
};

/**
 * Mise à jour des informations de l'espace famille d'un défunt.
 *
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const logInEF = (body) => {
    const url = `${process.env.REACT_APP_OEF_FAMILY_URL?.replace(/\/$/, '')}/api-v1/login_check`
    return postRequestWithoutToken(url, body)
};

/**
 * Mise à jour des informations de l'espace famille d'un défunt en local.
 *
 * @param string $token Jeton d'authentification.
 * @param object $body Corps de la requête.
 * @returns Response | null Réponse de la requête à l'API.
 */
export const createEF = (body, token) => {
    const url = `/api-v1/deceased/ef/create`
    return postRequest(url, body, token)
};

/**
 * Lecture des informations du user de l'espace famille du défunt.
 *
 * @param int $id L'id du défunt.
 * @returns Response|null Réponse de la requête.
 */
export const getDeceasedEfUserEmail = (id, token) => {
    const url =`/api-v1/deceased/${id}/ef-user/infos`
    return getRequest(url,token)
};